import { useEffect, useState, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkle, Sparkles, Clock, Volume2, VolumeX, ChevronRight, Send, Loader2,
  Home, LogIn, UserPlus, Languages, Crown, Flame, Moon, CheckCircle2,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useWakeLock } from "@/hooks/useWakeLock";
import { MiniTutorial } from "@/components/MiniTutorial";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LECTURA_TUTORIAL_STEPS = [
  {
    emoji: "🎴",
    title: "Tu lectura en vivo",
    desc: "CrisAngel te guía fase por fase: Baraja Española, Tarot y (en la Completa) Runas. En cada fase debes completar todas las tiradas para avanzar — verás el contador de tiradas con puntitos.",
  },
  {
    emoji: "👆",
    title: "Tira las cartas",
    desc: "Toca el botón dorado de tirar cartas en cada fase. Las cartas se revelan con animación y CrisAngel interpreta lo que ve, con su voz.",
  },
  {
    emoji: "🔊",
    title: "Voz e idioma",
    desc: "Puedes activar/silenciar la voz con el botón de la bocina y cambiar el idioma de la lectura con el selector en la parte superior.",
  },
  {
    emoji: "❓",
    title: "Preguntas en vivo",
    desc: "En la Lectura Completa podrás hacer preguntas en vivo y CrisAngel las responde al momento. Al final verás los trabajos recomendados para tu situación.",
  },
];

const LANGUAGES = [
  { code: "es", name: "Español", voice: "es" },
  { code: "en", name: "English", voice: "en" },
  { code: "pt", name: "Português", voice: "pt" },
  { code: "fr", name: "Français", voice: "fr" },
  { code: "it", name: "Italiano", voice: "it" },
  { code: "de", name: "Deutsch", voice: "de" },
];

const PHASE_INFO = {
  spanish_cards: { label: "Baraja Española", icon: "🃏", color: "from-amber-500 to-yellow-600", n: 9 },
  tarot: { label: "Tarot", icon: "🔮", color: "from-purple-500 to-fuchsia-600", n: 9 },
  predictions: { label: "Predicciones", icon: "⭐", color: "from-sky-500 to-cyan-600", n: 0 },
  additional: { label: "Lectura Adicional", icon: "🌙", color: "from-indigo-500 to-purple-600", n: 0 },
  questions_live: { label: "Preguntas en Vivo", icon: "💬", color: "from-emerald-500 to-teal-600", n: 0 },
  runes: { label: "Runas", icon: "ᚱ", color: "from-orange-500 to-red-600", n: 9 },
  summary: { label: "Cierre", icon: "✨", color: "from-[#d4af37] to-amber-500", n: 0 },
};

const PHASES_BY_TYPE = {
  normal: ["spanish_cards", "tarot", "summary"],
  complete: ["spanish_cards", "tarot", "predictions", "additional", "questions_live", "runes", "summary"],
};

// Sanitize text for TTS (remove markdown chars that voice should not speak)
const cleanForSpeech = (text) => {
  if (!text) return "";
  return String(text)
    .replace(/[*_#`~\[\]<>{}|]/g, "")  // markdown / brackets
    .replace(/\$+/g, "")                 // dollar signs
    .replace(/&[a-z]+;/gi, "")           // html entities
    .replace(/[•►◆◇★☆]/g, "")            // decorative bullets
    .replace(/\s{2,}/g, " ")
    .trim();
};

// Cache the voices Promise so we only resolve it once. On iOS Safari the
// voices list is empty on first call and arrives via `voiceschanged`.
let _voicesPromise = null;
const ensureVoices = () => {
  if (_voicesPromise) return _voicesPromise;
  _voicesPromise = new Promise((resolve) => {
    const synth = typeof window !== "undefined" ? window.speechSynthesis : null;
    if (!synth) { resolve([]); return; }
    const initial = synth.getVoices?.() || [];
    if (initial.length > 0) { resolve(initial); return; }
    const handler = () => {
      const v = synth.getVoices?.() || [];
      if (v.length > 0) {
        try { synth.removeEventListener("voiceschanged", handler); } catch { /* noop */ }
        resolve(v);
      }
    };
    try { synth.addEventListener("voiceschanged", handler); } catch { /* noop */ }
    // Fallback: resolve after 2s even if no voiceschanged fires (some
    // Android browsers ship voices late but don't emit the event).
    setTimeout(() => {
      try { synth.removeEventListener("voiceschanged", handler); } catch { /* noop */ }
      resolve(synth.getVoices?.() || []);
    }, 2000);
  });
  return _voicesPromise;
};

// Mobile audio unlock: iOS Safari requires the FIRST speechSynthesis.speak()
// to happen inside a user gesture. We attach a one-time listener that fires
// a silent utterance the first time the user taps/clicks anywhere.
let _speechUnlocked = false;
const primeSpeechOnce = () => {
  if (_speechUnlocked || typeof window === "undefined") return;
  const synth = window.speechSynthesis;
  if (!synth) return;
  try {
    const u = new SpeechSynthesisUtterance(" ");
    u.volume = 0;          // silent priming
    u.rate = 1;
    synth.speak(u);
    _speechUnlocked = true;
  } catch { /* noop */ }
};
if (typeof document !== "undefined" && typeof window !== "undefined") {
  const handler = () => { primeSpeechOnce(); };
  document.addEventListener("touchstart", handler, { once: true, passive: true });
  document.addEventListener("click", handler, { once: true });
}

// Speak using browser TTS — async so it can await voice loading on iOS.
const speakText = async (text, langCode, enabled) => {
  if (!enabled || !text) return;
  try {
    const synth = window.speechSynthesis;
    if (!synth) return;
    synth.cancel();
    const clean = cleanForSpeech(text);
    if (!clean) return;
    // Ensure speech is unlocked (no-op if already done).
    primeSpeechOnce();
    // Wait up to 2s for voices to load on mobile.
    const voices = await ensureVoices();
    const u = new SpeechSynthesisUtterance(clean);
    const lang = langCode === "en" ? "en-US" : "es-ES";
    u.lang = lang;
    u.rate = 0.95;
    u.pitch = 0.9;
    u.volume = 1.0;  // explicit max volume for mobile
    const prefix = lang.split("-")[0];
    const match = voices.find((v) => (v.lang || "").toLowerCase().startsWith(prefix));
    if (match) u.voice = match;
    // Some Android Chrome builds need a tiny delay after cancel() before speak.
    setTimeout(() => {
      try { synth.speak(u); } catch { /* ignore */ }
    }, 60);
  } catch { /* ignore */ }
};

// Suit metadata for the Spanish deck — gives each card a recognizable symbol
const SPANISH_SUIT_META = {
  Oros: { sym: "🟡", grad: "from-amber-500 to-yellow-700", ring: "border-amber-300/70" },
  Copas: { sym: "🏆", grad: "from-rose-500 to-red-700", ring: "border-rose-300/70" },
  Espadas: { sym: "⚔️", grad: "from-sky-500 to-indigo-700", ring: "border-sky-300/70" },
  Bastos: { sym: "🌿", grad: "from-emerald-500 to-green-800", ring: "border-emerald-300/70" },
};

// Major-arcana shortcuts → emoji (lightweight illustration)
const TAROT_GLYPH = {
  "El Loco": "🃏", "El Mago": "✨", "La Sacerdotisa": "🌙", "La Emperatriz": "👑",
  "El Emperador": "🏛️", "El Hierofante": "📜", "Los Enamorados": "💞", "El Carro": "🏇",
  "La Justicia": "⚖️", "El Ermitaño": "🔦", "La Rueda de la Fortuna": "🎡", "La Fuerza": "🦁",
  "El Colgado": "🙃", "La Muerte": "💀", "La Templanza": "🕊️", "El Diablo": "😈",
  "La Torre": "🗼", "La Estrella": "⭐", "La Luna": "🌕", "El Sol": "☀️",
  "El Juicio": "📯", "El Mundo": "🌍",
};

const RUNE_GLYPHS = ["ᚠ","ᚢ","ᚦ","ᚨ","ᚱ","ᚲ","ᚷ","ᚹ","ᚺ","ᚾ","ᛁ","ᛃ","ᛇ","ᛈ","ᛉ","ᛋ","ᛏ","ᛒ","ᛖ","ᛗ","ᛚ","ᛜ","ᛟ","ᛞ"];

// Card visual for Spanish + Tarot decks. Supports reversed orientation.
const Card = ({ name, color, delay = 0, reversed = false }) => {
  // Detect Spanish suit + tarot major arcana
  const m = name.match(/^([\w\s]+?) de (Oros|Copas|Espadas|Bastos)$/);
  const isSpanish = !!m;
  const suit = m?.[2];
  const rank = m?.[1] || name;
  const meta = isSpanish ? SPANISH_SUIT_META[suit] : null;
  const glyph = isSpanish ? meta.sym : (TAROT_GLYPH[name] || "✨");
  const grad = isSpanish ? meta.grad : color;
  const ring = isSpanish ? meta.ring : "border-[#d4af37]/60";
  return (
    <motion.div
      initial={{ rotateY: 180, opacity: 0, y: 40 }}
      animate={{ rotateY: 0, opacity: 1, y: 0 }}
      transition={{ duration: 0.9, delay, type: "spring", stiffness: 60 }}
      className="relative w-28 h-44 md:w-32 md:h-48 rounded-xl shadow-2xl flex-shrink-0"
      style={{ perspective: 1000 }}
    >
      <div className={`absolute inset-0 rounded-xl bg-gradient-to-br ${grad} border-2 ${ring} p-2 flex flex-col items-center justify-between text-center overflow-hidden`}
        style={{ transform: reversed ? "rotate(180deg)" : "none", transition: "transform 0.6s" }}>
        <div className="text-white font-serif text-[10px] md:text-xs leading-tight">{isSpanish ? rank : name}</div>
        <div className="text-4xl md:text-5xl drop-shadow">{glyph}</div>
        <div className="text-white font-serif text-[10px] md:text-xs leading-tight">{isSpanish ? suit : "Arcano"}</div>
      </div>
      {reversed && (
        <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 text-[9px] uppercase tracking-widest text-amber-300 bg-black/80 px-2 py-0.5 rounded-full border border-amber-400/50 whitespace-nowrap">
          Invertida
        </span>
      )}
    </motion.div>
  );
};

const RuneStone = ({ name, delay = 0, reversed = false }) => {
  const idx = RUNE_GLYPHS.length > 0 ? Math.abs([...name].reduce((a, c) => a + c.charCodeAt(0), 0)) % RUNE_GLYPHS.length : 0;
  return (
    <motion.div
      initial={{ scale: 0, rotate: -45 }}
      animate={{ scale: 1, rotate: 0 }}
      transition={{ duration: 0.8, delay, type: "spring" }}
      className="relative w-24 h-28 md:w-28 md:h-32 flex-shrink-0"
    >
      <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-stone-700 via-stone-800 to-stone-900 border border-orange-400/40 shadow-2xl shadow-orange-500/30 flex flex-col items-center justify-center p-2"
        style={{ transform: reversed ? "rotate(180deg)" : "none", transition: "transform 0.6s" }}>
        <div className="text-orange-300 font-serif text-4xl mb-1">{RUNE_GLYPHS[idx]}</div>
        <div className="text-orange-100 text-[10px] font-semibold">{name.split(" ")[0]}</div>
        <div className="text-orange-200/70 text-[8px] text-center leading-tight">{name.split("(")[1]?.replace(")", "")}</div>
      </div>
      {reversed && (
        <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 text-[9px] uppercase tracking-widest text-orange-300 bg-black/80 px-2 py-0.5 rounded-full border border-orange-400/50 whitespace-nowrap">
          Invertida
        </span>
      )}
    </motion.div>
  );
};

export default function LecturaOnline() {
  const { user, authHeader } = useAuth();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState(null);
  const [drawing, setDrawing] = useState(false);
  // Index of the event currently being viewed (null = latest). Lets the user
  // browse previously-drawn cards / answers without losing their place.
  const [viewIdx, setViewIdx] = useState(null);
  const [events, setEvents] = useState([]);
  const [now, setNow] = useState(() => Date.now());
  const [voiceOn, setVoiceOn] = useState(true);
  const [language, setLanguage] = useState("es");
  const [recommendation, setRecommendation] = useState(null);
  const [recommendationLoading, setRecommendationLoading] = useState(false);
  const [works, setWorks] = useState([]);
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [selectedWork, setSelectedWork] = useState(null);
  const [workQuestion, setWorkQuestion] = useState("");
  const [workAnswer, setWorkAnswer] = useState("");
  const [askingWork, setAskingWork] = useState(false);
  const [ratingStars, setRatingStars] = useState(0);
  const [ratingComment, setRatingComment] = useState("");
  const [ratingSent, setRatingSent] = useState(false);
  const [ratingSending, setRatingSending] = useState(false);
  const [showWorkInterestModal, setShowWorkInterestModal] = useState(false);
  const eventsEndRef = useRef(null);

  // Form
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    full_name: "", dob: "", location: "",
    children_info: "", partner_info: "", extra_details: "",
  });

  // Auto-fill name from registered user (editable) once user loads
  useEffect(() => {
    if (user?.name && !form.full_name) {
      setForm((f) => ({ ...f, full_name: user.name }));
    }
    // eslint-disable-next-line
  }, [user]);
  const [questions, setQuestions] = useState(["", "", "", "", "", ""]);
  const [submitting, setSubmitting] = useState(false);

  // Live question (only for complete)
  const [liveQ, setLiveQ] = useState("");
  const [asking, setAsking] = useState(false);

  // ===== Session loader =====
  const loadSession = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    try {
      const r = await axios.get(`${API}/users/reading/active`, { headers: authHeader() });
      if (r.data?.active) {
        const s = r.data.session;
        setSession(s);
        setEvents(s.events || []);
        if (s.status === "granted") setShowForm(true);
        if (s.status === "in_progress") {
          setShowForm(false);
          // determine current phase from events
          const phases = PHASES_BY_TYPE[s.reading_type] || [];
          const doneKinds = new Set((s.events || []).map(e => e.phase));
          const next = phases.find((p) => !doneKinds.has(p) && p !== "summary") || "summary";
          setPhase(next);
        }
      } else {
        setSession(null);
        setShowForm(false);
        if (r.data?.just_expired) {
          toast.info("Tu sesión expiró");
          setRecommendation(null);
        }
      }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [user, authHeader]);

  useEffect(() => { loadSession(); }, [loadSession]);

  // Auto-fetch works + recommendation when reaching summary/Cierre phase or completed status
  useEffect(() => {
    if (!session) return;
    const atClosing = phase === "summary" || session.status === "completed";
    if (!atClosing || works.length > 0) return;
    setRecommendationLoading(true);
    // Fire both independently so works render even while recommendation is still loading.
    axios.get(`${API}/users/reading/works`, { headers: authHeader() })
      .then((r) => {
        if (!r?.data) return;
        const list = Array.isArray(r.data) ? r.data : (r.data.works || []);
        setWorks(list);
        if (r.data.whatsapp_number) setWhatsappNumber(r.data.whatsapp_number);
      }).catch(() => {});
    axios.get(`${API}/users/reading/recommendation?language=${language}`, { headers: authHeader() })
      .then((r) => {
        if (r?.data?.recommendation) setRecommendation(r.data.recommendation);
        else if (r?.data?.text) setRecommendation(r.data.text);
      })
      .catch(() => {})
      .finally(() => setRecommendationLoading(false));
    // eslint-disable-next-line
  }, [phase, session?.status]);

  // Refetch on window focus + every 8s when waiting/no session
  useEffect(() => {
    const onFocus = () => loadSession();
    window.addEventListener("focus", onFocus);
    let poll = null;
    if (!session || session.status === "granted" || session.status === "expired") {
      poll = window.setInterval(() => loadSession(), 8000);
    }
    return () => {
      window.removeEventListener("focus", onFocus);
      if (poll) window.clearInterval(poll);
    };
  }, [session, loadSession]);

  // ===== Timer (tick every second via state counter) =====
  useEffect(() => {
    const interval = window.setInterval(() => {
      window.dispatchEvent(new CustomEvent("lectura-tick"));
    }, 1000);
    const handler = () => setNow(Date.now());
    window.addEventListener("lectura-tick", handler);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("lectura-tick", handler);
    };
  }, []);

  // Auto-expire / fetch recommendation when time's up.
  // Only finalize when document is visible — otherwise the browser may have
  // suspended setInterval (screen sleep) and the time jump shouldn't end the
  // reading; let the user resume and rely on a real timer tick.
  useEffect(() => {
    if (!session || session.status !== "in_progress" || !session.expires_at) return undefined;
    if (document.hidden) return undefined;
    const ms = new Date(session.expires_at).getTime() - now;
    if (ms <= 0) {
      finalizeSession();
    }
    return undefined;
  }, [now, session]); // eslint-disable-line

  // Keep the screen awake while the reading is in progress so phones/tablets
  // don't turn off during the audio (which caused premature stop).
  useWakeLock(!!(session && session.status === "in_progress"));

  const timeLeft = () => {
    if (!session?.expires_at) return null;
    const ms = new Date(session.expires_at).getTime() - now;
    if (ms <= 0) return { mm: 0, ss: 0, expired: true };
    return { mm: Math.floor(ms / 60000), ss: Math.floor((ms % 60000) / 1000), expired: false };
  };

  // ===== Start reading (submit form) =====
  const submitForm = async () => {
    const qs = questions.map(q => q.trim()).filter(Boolean);
    if (qs.length < 3) return toast.error("Mínimo 3 preguntas. Recomendado 6 o más.");
    const maxQ = session?.reading_type === "complete" ? 24 : 12;
    if (qs.length > maxQ) return toast.error(`Máximo ${maxQ} preguntas para esta lectura`);
    if (qs.length < 6) {
      const ok = window.confirm("⚠️ Recomendado 6 preguntas o más para una lectura más completa. ¿Continuar con menos?");
      if (!ok) return;
    }
    if (!form.full_name || !form.dob || !form.location) return toast.error("Completa nombre, fecha de nacimiento y residencia");
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/users/reading/${session.id}/start`,
        { form_data: form, questions: qs }, { headers: authHeader() });
      setSession(r.data.session);
      setEvents([]);
      setShowForm(false);
      const phases = PHASES_BY_TYPE[r.data.session.reading_type];
      setPhase(phases[0]);
      toast.success("Lectura iniciada. Empecemos.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error iniciando");
    } finally { setSubmitting(false); }
  };

  // ===== Draw a phase =====
  const drawPhase = async () => {
    if (!session || !phase) return;
    if (phase === "summary") return; // summary uses closing screen, not draws
    // Stop any audio currently playing so it doesn't overlap the new draw
    try { window.speechSynthesis?.cancel(); } catch { /* ignore */ }
    setDrawing(true);
    try {
      const info = PHASE_INFO[phase];
      const r = await axios.post(`${API}/users/reading/${session.id}/draw`,
        { phase, language, n_cards: info.n || 9 },
        { headers: authHeader() });
      const ev = r.data;
      setEvents((e) => [...e, ev]);
      setViewIdx(null); // jump to latest
      speakText(ev.interpretation, language, voiceOn);
      setTimeout(() => eventsEndRef.current?.scrollIntoView({ behavior: "smooth" }), 200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error en la lectura");
    } finally { setDrawing(false); }
  };

  // ===== Live question =====
  const askLive = async () => {
    if (!liveQ.trim()) return;
    setAsking(true);
    try {
      const r = await axios.post(`${API}/users/reading/${session.id}/ask`,
        { question: liveQ, language },
        { headers: authHeader() });
      const ev = r.data;
      setEvents((e) => [...e, ev]);
      speakText(ev.interpretation, language, voiceOn);
      setLiveQ("");
      setTimeout(() => eventsEndRef.current?.scrollIntoView({ behavior: "smooth" }), 200);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setAsking(false); }
  };

  // Count draws per phase (Q&A doesn't count)
  const drawsInPhase = (p) => events.filter(e => e.phase === p && e.kind !== "Pregunta en vivo" && !e.skipped).length;
  const requiredFor = (p) => {
    const cfg = session?.draws_per_phase || {};
    return Number(cfg[p]) || 0;
  };
  const canAdvance = (p) => {
    if (!p || p === "summary") return false;
    if (p === "questions_live") return true;
    return drawsInPhase(p) >= requiredFor(p);
  };

  // ===== Advance phase =====
  const advancePhase = (skipConfirm) => {
    if (!session) return;
    // Stop any audio currently playing
    try { window.speechSynthesis?.cancel(); } catch { /* ignore */ }
    const phases = PHASES_BY_TYPE[session.reading_type];
    const idx = phases.indexOf(phase);
    if (idx < 0) return;
    const req = requiredFor(phase);
    const done = drawsInPhase(phase);
    if (phase !== "questions_live" && phase !== "summary" && done < req && !skipConfirm) {
      const ok = window.confirm(`Te faltan ${req - done} tirada(s) en esta fase. ¿Seguro que quieres saltar el resto?`);
      if (!ok) return;
    }
    if (idx >= phases.length - 2) {
      finalizeSession();
      return;
    }
    setPhase(phases[idx + 1]);
    setViewIdx(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ===== Finalize session =====
  const finalizeSession = async () => {
    if (!session) return;
    try {
      await axios.post(`${API}/users/reading/${session.id}/complete`, {}, { headers: authHeader() });
      // Engagement: prompt PWA install right after a successful reading
      try { window.dispatchEvent(new CustomEvent("ca:pwa-suggest", { detail: { source: "reading-complete" } })); } catch { /* noop */ }
    } catch { /* ignore */ }
    setSession((s) => s ? { ...s, status: "completed" } : null);
    setPhase("summary");
    // Fetch recommendation intro + works catalog
    setRecommendationLoading(true);
    try {
      const [recR, worksR] = await Promise.all([
        axios.get(`${API}/users/reading/recommendation?language=${language}`, { headers: authHeader() }),
        axios.get(`${API}/users/reading/works`, { headers: authHeader() }),
      ]);
      setRecommendation(recR.data?.text || "");
      setWorks(worksR.data?.works || []);
      setWhatsappNumber(worksR.data?.whatsapp_number || "");
      speakText(recR.data?.text, language, voiceOn);
    } catch { setRecommendation(""); }
    finally { setRecommendationLoading(false); }
  };

  // ===========================================================
  // RENDER
  // ===========================================================
  const tl = timeLeft();

  // ----- Not logged in -----
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 py-12 relative" data-testid="lectura-not-logged">
        <div className="max-w-md w-full glass-dark rounded-3xl p-8 text-center border border-purple-500/30">
          <div className="flex justify-center mb-4">
            <div className="relative">
              <div className="absolute inset-0 bg-purple-500/40 blur-2xl rounded-full" />
              <Sparkle className="w-16 h-16 text-purple-300 relative animate-spin-slow" />
            </div>
          </div>
          <h1 className="font-serif text-3xl text-white mb-2">Lectura Online</h1>
          <p className="text-neutral-400 text-sm mb-6">Para acceder necesitas estar registrado en CrisAngel Astral.</p>
          <div className="flex flex-col gap-2">
            <Link to="/ingresar" data-testid="goto-login" className="btn-gold py-3 rounded-full uppercase text-xs tracking-widest flex items-center justify-center gap-2"><LogIn className="w-4 h-4" /> Iniciar sesión</Link>
            <Link to="/registro" data-testid="goto-register" className="btn-outline-gold py-3 rounded-full uppercase text-xs tracking-widest flex items-center justify-center gap-2"><UserPlus className="w-4 h-4" /> Crear cuenta</Link>
            <Link to="/" className="text-xs text-neutral-500 hover:text-[#d4af37] mt-4 inline-flex items-center justify-center gap-1"><Home className="w-3 h-3" /> Volver al inicio</Link>
          </div>
        </div>
        {/* Floating login button (always visible at bottom-right) */}
        <Link to="/ingresar" className="fixed bottom-6 right-6 z-50 bg-gradient-to-br from-[#d4af37] to-amber-600 text-black px-4 py-3 rounded-full shadow-2xl shadow-[#d4af37]/40 text-xs uppercase tracking-widest font-semibold flex items-center gap-2 hover:scale-105 transition-transform">
          <LogIn className="w-4 h-4" /> Acceder
        </Link>
      </div>
    );
  }

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 text-purple-300 animate-spin" /></div>;
  }

  // ===== Ask about a work =====
  const askAboutWork = async (workId) => {
    if (!workQuestion.trim()) return;
    setAskingWork(true);
    setWorkAnswer("");
    try {
      const r = await axios.post(`${API}/users/reading/${session.id}/ask-work`,
        { work_id: workId, question: workQuestion, language },
        { headers: authHeader() });
      setWorkAnswer(r.data?.answer || "");
      speakText(r.data?.answer, language, voiceOn);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setAskingWork(false); }
  };

  const whatsappLink = (work) => {
    const name = session?.form_data?.full_name || user?.name || "";
    const text = encodeURIComponent(
      `Hola CrisAngel, soy ${name}. Acabo de terminar mi lectura online y me interesa el trabajo "${work.name}" (${work.price_mxn} MXN / ${work.price_usd} USD). ¿Cómo procedemos?`
    );
    return `https://wa.me/${whatsappNumber || "526622870805"}?text=${text}`;
  };

  const submitRating = async () => {
    if (!ratingStars || ratingStars < 1 || ratingStars > 5) return;
    if (!session?.id) return;
    setRatingSending(true);
    try {
      await axios.post(`${API}/users/reading/${session.id}/rate`,
        { stars: ratingStars, comment: ratingComment },
        { headers: authHeader() });
      setRatingSent(true);
      toast.success("¡Gracias por tu calificación!");
      // Open the work-interest prompt right after rating is captured
      setTimeout(() => setShowWorkInterestModal(true), 600);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al enviar"); }
    finally { setRatingSending(false); }
  };

  const sendWorkInterestToAdmin = async () => {
    const text = "Me interesa un trabajo de hechicería.";
    try {
      // Send via chat
      await axios.post(`${API}/users/messages`, { body: text, kind: "text" }, { headers: authHeader() });
    } catch { /* ignore */ }
    // Open WhatsApp pre-filled (admin's WhatsApp)
    try {
      const wa = "https://wa.me/526622870805?text=" + encodeURIComponent(text);
      window.open(wa, "_blank", "noopener");
    } catch { /* ignore */ }
    setShowWorkInterestModal(false);
    toast.success("Mensaje enviado al administrador 🔮");
  };

  // ----- Logged but no reading granted -----
  if (!session || session.status === "completed" || session.status === "expired" || phase === "summary") {
    // If we have a completed/closing session, show closing screen with works
    if (session && (session.status === "completed" || phase === "summary")) {
      return (
        <div className="min-h-screen pt-24 pb-16 px-4 md:px-6" data-testid="lectura-completed">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-8">
              <Crown className="w-14 h-14 text-[#d4af37] mx-auto mb-3" />
              <h1 className="font-serif text-3xl md:text-4xl text-white mb-2">Tu lectura ha concluido</h1>
              <p className="text-neutral-400 text-sm">Las cartas han hablado. Ahora te muestro la solución que puedo ofrecerte.</p>
            </div>

            {recommendationLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 text-[#d4af37] animate-spin" /></div>
            ) : recommendation && (
              <div className="glass-dark border border-[#d4af37]/40 rounded-2xl p-5 mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <Flame className="w-5 h-5 text-orange-400" />
                  <h3 className="font-serif text-lg text-white">Mi recomendación</h3>
                </div>
                <p className="text-neutral-200 whitespace-pre-wrap leading-relaxed text-sm">{recommendation}</p>
                {voiceOn && <button onClick={() => speakText(recommendation, language, true)} className="mt-2 text-xs text-purple-300 hover:underline flex items-center gap-1"><Volume2 className="w-3 h-3" /> Repetir voz</button>}
              </div>
            )}

            {/* Works catalog */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {works.map((w) => {
                const isPurple = w.color === "purple";
                const Icon = isPurple ? Crown : Flame;
                const cls = isPurple ? "from-purple-600 to-fuchsia-700 border-purple-400/40" : "from-orange-600 to-red-700 border-orange-400/40";
                const isSel = selectedWork?.id === w.id;
                return (
                  <div key={w.id} data-testid={`work-${w.id}`} className={`relative glass-dark border-2 rounded-2xl p-5 cursor-pointer transition-all ${isSel ? "border-[#d4af37] shadow-2xl shadow-[#d4af37]/20" : "border-white/10 hover:border-[#d4af37]/50"}`} onClick={() => { setSelectedWork(w); setWorkAnswer(""); setWorkQuestion(""); }}>
                    <div className={`absolute -top-3 left-4 px-3 py-0.5 rounded-full bg-gradient-to-r ${cls} text-white text-[10px] uppercase tracking-widest font-bold flex items-center gap-1`}>
                      <Icon className="w-3 h-3" /> {w.tagline.split(",")[0]}
                    </div>
                    <h3 className="font-serif text-2xl text-white mb-1">{w.name}</h3>
                    <p className="text-xs text-neutral-400 mb-3">{w.tagline}</p>
                    <div className="flex items-baseline gap-2 mb-3">
                      <span className="font-serif text-3xl text-[#d4af37]">{w.price_mxn.toLocaleString("es-MX")}</span>
                      <span className="text-sm text-neutral-400">MXN</span>
                      <span className="text-xs text-neutral-500">/ {w.price_usd} USD</span>
                    </div>
                    <p className="text-[11px] text-emerald-300 mb-3 flex items-center gap-1"><Clock className="w-3 h-3" /> {w.duration}</p>
                    <div className="space-y-1 mb-3">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Beneficios principales</div>
                      {w.benefits.slice(0, 3).map((b, i) => (
                        <div key={i} className="text-xs text-neutral-300 flex items-start gap-1.5"><Sparkle className="w-3 h-3 text-[#d4af37] mt-0.5 flex-shrink-0" /> {b}</div>
                      ))}
                    </div>
                    {isSel && (
                      <div className="border-t border-white/10 pt-3 mt-3 space-y-2 text-xs">
                        <div>
                          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Todo lo que incluye</div>
                          {w.what_it_includes.map((it, i) => <div key={i} className="text-neutral-300 flex items-start gap-1.5">✓ <span>{it}</span></div>)}
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Ideal para</div>
                          <div className="text-amber-200">{w.best_for}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Formas de pago</div>
                          <div className="text-neutral-300">Transferencia bancaria, PayPal, depósito en efectivo. Apartas con el 50% y pagas el resto al iniciar el trabajo.</div>
                        </div>
                        <a href={whatsappLink(w)} target="_blank" rel="noopener noreferrer" data-testid={`wa-${w.id}`} className="btn-gold mt-3 inline-flex items-center justify-center gap-2 py-2.5 px-4 rounded-full uppercase text-xs tracking-widest w-full">
                          Apartar por WhatsApp con CrisAngel
                        </a>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Q&A about works */}
            <div className="glass-dark border border-purple-500/20 rounded-2xl p-5 mb-6">
              <h3 className="font-serif text-lg text-white mb-2 flex items-center gap-2"><Sparkle className="w-4 h-4 text-purple-300" /> ¿Dudas sobre un trabajo?</h3>
              <p className="text-xs text-neutral-400 mb-3">Pregúntame lo que quieras y te ayudo a elegir el mejor para ti.</p>
              <div className="flex gap-2 mb-3">
                <Input data-testid="work-q-input" value={workQuestion} onChange={(e) => setWorkQuestion(e.target.value)} placeholder="Ej: ¿Cuál me conviene si tengo problemas de pareja?" className="bg-black/40 border-white/10 text-white" onKeyDown={(e) => e.key === "Enter" && askAboutWork(selectedWork?.id || "general")} />
                <button data-testid="work-q-send" disabled={askingWork || !workQuestion.trim()} onClick={() => askAboutWork(selectedWork?.id || "general")} className="btn-gold px-5 rounded-full uppercase text-xs tracking-widest flex items-center gap-1 disabled:opacity-50">
                  {askingWork ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-3.5 h-3.5" /> Preguntar</>}
                </button>
              </div>
              {workAnswer && (
                <div className="bg-black/40 border border-purple-500/30 rounded p-3 text-sm">
                  <div className="text-purple-300 font-semibold text-xs uppercase tracking-widest mb-1.5">CrisAngel responde:</div>
                  <div className="text-neutral-200 whitespace-pre-wrap">{workAnswer}</div>
                </div>
              )}
            </div>

            {/* User rating 1-5 stars */}
            <div data-testid="lectura-rating" className="glass-dark border border-[#d4af37]/30 rounded-2xl p-5 mb-6">
              <h3 className="font-serif text-lg text-white mb-2 flex items-center gap-2">⭐ ¿Cómo calificarías tu lectura?</h3>
              {session?.rating || ratingSent ? (
                <div className="text-center py-3">
                  <div className="text-3xl mb-1">
                    {Array.from({length: 5}).map((_, i) => (
                      <span key={i} className={i < (session?.rating?.stars || ratingStars) ? "text-amber-400" : "text-neutral-700"}>★</span>
                    ))}
                  </div>
                  <p className="text-xs text-emerald-300">¡Gracias por tu calificación! Tu opinión es muy valiosa.</p>
                </div>
              ) : (
                <>
                  <p className="text-xs text-neutral-400 mb-3">Tu calificación me ayuda a mejorar y a seguir guiando a otros desde la luz.</p>
                  <div className="flex items-center justify-center gap-1.5 mb-3" data-testid="rating-stars-row">
                    {[1,2,3,4,5].map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setRatingStars(n)}
                        data-testid={`star-${n}`}
                        className={`text-4xl transition-transform hover:scale-125 ${n <= ratingStars ? "text-amber-400" : "text-neutral-600"}`}
                        aria-label={`${n} estrella${n>1?'s':''}`}
                      >★</button>
                    ))}
                  </div>
                  <textarea
                    data-testid="rating-comment"
                    value={ratingComment}
                    onChange={(e) => setRatingComment(e.target.value)}
                    rows={2}
                    placeholder="Comentario (opcional)"
                    className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-neutral-500 focus:outline-none focus:border-[#d4af37]/40"
                  />
                  <button
                    onClick={submitRating}
                    disabled={!ratingStars || ratingSending}
                    data-testid="rating-submit"
                    className="btn-gold mt-3 w-full py-2.5 rounded-full uppercase text-xs tracking-widest disabled:opacity-40"
                  >
                    {ratingSending ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Enviar Calificación"}
                  </button>
                </>
              )}
            </div>

            <div className="text-center">
              <Link to="/" className="text-xs text-neutral-500 hover:text-[#d4af37] inline-flex items-center gap-1"><Home className="w-3 h-3" /> Volver al inicio</Link>
            </div>
          </div>
          {/* Work-interest modal (after rating) */}
          {showWorkInterestModal && (
            <div data-testid="work-interest-modal" className="fixed inset-0 z-[180] bg-black/85 backdrop-blur-md flex items-center justify-center p-4" onClick={() => setShowWorkInterestModal(false)}>
              <div className="bg-gradient-to-b from-[#1a1a1a] to-[#0a0a0a] border-2 border-[#d4af37]/50 rounded-2xl p-6 max-w-md w-full text-center shadow-2xl shadow-amber-500/30" onClick={(e) => e.stopPropagation()}>
                <div className="text-5xl mb-3">🔮</div>
                <h3 className="font-serif text-2xl text-white mb-2">¿Te interesa un trabajo de hechicería?</h3>
                <p className="text-sm text-neutral-300 mb-5">
                  Si las cartas te revelaron que necesitas atención espiritual, puedo prepararte un trabajo personalizado.
                  Hazme saber tu interés y te contacto de inmediato por WhatsApp y chat.
                </p>
                <div className="flex flex-col sm:flex-row gap-2 justify-center">
                  <button data-testid="work-interest-yes" onClick={sendWorkInterestToAdmin} className="btn-gold px-6 py-2.5 rounded-full uppercase text-xs tracking-widest">Sí, me interesa</button>
                  <button data-testid="work-interest-no" onClick={() => setShowWorkInterestModal(false)} className="btn-outline-gold px-6 py-2.5 rounded-full uppercase text-xs tracking-widest">No por ahora</button>
                </div>
                <p className="text-[10px] text-neutral-500 mt-4 italic">Al confirmar, se enviará: "Me interesa un trabajo de hechicería."</p>
              </div>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="min-h-screen flex items-center justify-center px-6 py-12" data-testid="lectura-waiting">
        <div className="max-w-md w-full glass-dark rounded-3xl p-8 text-center border border-purple-500/30">
          <Moon className="w-16 h-16 text-purple-300 mx-auto mb-4 animate-pulse" />
          <h1 className="font-serif text-3xl text-white mb-2">Aún no tienes una lectura habilitada</h1>
          <p className="text-neutral-400 text-sm mb-6">
            El administrador debe habilitarte y elegir el tipo de lectura. Recibirás una notificación cuando esté lista.
          </p>
          <button data-testid="lectura-refresh" onClick={() => loadSession()} className="btn-gold py-2.5 px-6 rounded-full uppercase text-xs tracking-widest inline-flex items-center gap-2"><Loader2 className="w-4 h-4" /> Verificar de nuevo</button>
          <div className="mt-3">
            <Link to="/mi-cuenta" className="btn-outline-gold py-2.5 px-6 rounded-full uppercase text-xs tracking-widest inline-flex items-center gap-2">Ver Mi Cuenta</Link>
          </div>
          <Link to="/" className="block text-xs text-neutral-500 hover:text-[#d4af37] mt-4 inline-flex items-center justify-center gap-1"><Home className="w-3 h-3" /> Volver al inicio</Link>
        </div>
      </div>
    );
  }

  // ----- Form before starting -----
  if (showForm && session.status === "granted") {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6" data-testid="lectura-form">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-6">
            <Sparkles className="w-12 h-12 text-purple-300 mx-auto mb-3" />
            <h1 className="font-serif text-3xl text-white">Formulario para tu Lectura Personalizada</h1>
            <p className="text-xs text-neutral-400 mt-2">🔒 Tu información es completamente confidencial.</p>
            <div className="text-[11px] text-[#d4af37] mt-1">
              {session.reading_label} · {session.duration_minutes} min
            </div>
          </div>

          {/* Guía paso a paso */}
          <div data-testid="form-guide" className="glass-dark border border-[#d4af37]/30 rounded-2xl p-5 mb-5">
            <div className="flex items-center gap-2 mb-2">
              <Sparkle className="w-4 h-4 text-[#d4af37]" />
              <h2 className="font-serif text-base text-white">Cómo llenar este formulario</h2>
            </div>
            <ol className="text-xs text-neutral-300 space-y-1.5 list-decimal list-inside leading-relaxed">
              <li><span className="text-white font-semibold">Datos personales:</span> tu nombre completo, fecha de nacimiento y residencia. Son la base energética de la lectura.</li>
              <li><span className="text-white font-semibold">Info familiar (opcional):</span> hijos y pareja/ex-pareja. Si tu duda involucra estas energías, incluye nombres y fechas. Si no aplica, déjalo en blanco.</li>
              <li><span className="text-white font-semibold">Detalles adicionales:</span> describe en confianza cualquier situación, sospecha, sueño o conflicto. <em className="text-amber-300">Cuanto más detalle me des, más precisa será tu lectura.</em></li>
              <li><span className="text-white font-semibold">Tus 6 preguntas (mínimo):</span> escribe las preguntas reales que quieres respondidas. Si necesitas más espacio puedes añadir hasta 12.</li>
              <li><span className="text-white font-semibold">Al pulsar &quot;Comenzar&quot;:</span> empieza el cronómetro y la sesión se vuelve interactiva. Las cartas se tiran en fases, con interpretación y voz.</li>
            </ol>
            <p className="mt-3 text-[11px] text-purple-300/90 flex items-start gap-1.5">
              <Sparkle className="w-3 h-3 mt-0.5 flex-shrink-0" />
              Tip: tómate tu tiempo. Una vez que comiences, el reloj corre y no podrás editar tus respuestas.
            </p>
          </div>

          <div className="glass-dark rounded-2xl p-6 space-y-4 border border-purple-500/20">
            <h3 className="font-serif text-lg text-white border-b border-white/10 pb-2">🌐 Idioma</h3>
            <div>
              <Label className="text-neutral-300 text-xs uppercase tracking-widest mb-1.5">Idioma de tu lectura (texto y voz)</Label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger data-testid="form-language" className="bg-black/40 border-white/10 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0a0a0a] border-[#d4af37]/30 text-white">
                  {LANGUAGES.map((l) => <SelectItem key={l.code} value={l.code}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-neutral-500 mt-1">CrisAngel interpretará tu lectura completa en este idioma.</p>
            </div>

            <h3 className="font-serif text-lg text-white border-b border-white/10 pb-2 mt-4">🧾 Datos personales</h3>
            <div>
              <Label className="text-neutral-300 text-xs uppercase tracking-widest mb-1.5">Nombre completo *</Label>
              <Input data-testid="form-full-name" value={form.full_name} onChange={(e) => setForm({...form, full_name: e.target.value})} className="bg-black/40 border-white/10 text-white" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-neutral-300 text-xs uppercase tracking-widest mb-1.5">Fecha de nacimiento *</Label>
                <Input data-testid="form-dob" type="date" value={form.dob} onChange={(e) => setForm({...form, dob: e.target.value})} className="bg-black/40 border-white/10 text-white" />
              </div>
              <div>
                <Label className="text-neutral-300 text-xs uppercase tracking-widest mb-1.5">Residencia actual *</Label>
                <Input data-testid="form-location" value={form.location} onChange={(e) => setForm({...form, location: e.target.value})} placeholder="Ciudad, País" className="bg-black/40 border-white/10 text-white" />
              </div>
            </div>

            <h3 className="font-serif text-lg text-white border-b border-white/10 pb-2 mt-4">👨‍👩‍👧 Información familiar (opcional)</h3>
            <div>
              <Label className="text-neutral-300 text-xs uppercase tracking-widest mb-1.5">Hijos (nombres y edades)</Label>
              <Input data-testid="form-children" value={form.children_info} onChange={(e) => setForm({...form, children_info: e.target.value})} placeholder="Ej: Ana, 5 años; Pedro, 8 años" className="bg-black/40 border-white/10 text-white" />
            </div>
            <div>
              <Label className="text-neutral-300 text-xs uppercase tracking-widest mb-1.5">Pareja / ex-pareja (nombre y fecha nac.)</Label>
              <Input data-testid="form-partner" value={form.partner_info} onChange={(e) => setForm({...form, partner_info: e.target.value})} placeholder="Ej: Juan Pérez, 15/03/1985" className="bg-black/40 border-white/10 text-white" />
            </div>

            <div>
              <Label className="text-neutral-300 text-xs uppercase tracking-widest mb-1.5">📝 Detalles adicionales (opcional)</Label>
              <Textarea data-testid="form-extra" rows={4} value={form.extra_details} onChange={(e) => setForm({...form, extra_details: e.target.value})} placeholder="Cuéntame cualquier detalle relevante: situaciones específicas, sospechas, sueños recurrentes, conflictos, etc. Cuanto más sepa, más certera será tu lectura." className="bg-black/40 border-white/10 text-white" />
            </div>

            <h3 className="font-serif text-lg text-white border-b border-white/10 pb-2 mt-4">🔮 Tus preguntas</h3>
            <p className="text-xs text-neutral-300">
              Mínimo <span className="text-[#d4af37] font-semibold">3 preguntas</span> · Recomendado <span className="text-amber-300 font-semibold">6 o más</span> ·
              Máximo <span className="text-purple-300 font-semibold">{session?.reading_type === "complete" ? 24 : 12}</span> {session?.reading_type === "complete" ? "(Lectura Runas)" : "(Lectura Normal)"}
            </p>
            <p className="text-[10px] text-amber-300/80">⚠️ Cuantas más preguntas reales me hagas, más se ajustará tu lectura a tu vida.</p>
            <details className="bg-black/30 border border-purple-500/20 rounded-lg p-2.5 text-xs text-neutral-300">
              <summary className="cursor-pointer text-purple-300 font-semibold">💡 Ejemplos de buenas preguntas</summary>
              <ul className="mt-2 ml-3 list-disc space-y-1 text-neutral-400">
                <li>¿Qué energía rodea a mi pareja en este momento?</li>
                <li>¿Hay alguien interfiriendo en mi relación con [nombre]?</li>
                <li>¿Cómo va mi situación económica los próximos 3 meses?</li>
                <li>¿Qué bloqueos kármicos cargo y cómo liberarlos?</li>
                <li>¿La persona X siente algo verdadero por mí?</li>
              </ul>
              <p className="mt-2 text-[10px] text-neutral-500 italic">Evita preguntas de sí/no. Las preguntas abiertas dan lecturas más profundas.</p>
            </details>
            <div className="space-y-2">
              {questions.map((q, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-xs text-[#d4af37] font-mono w-6">#{i+1}</span>
                  <Input data-testid={`question-${i}`} value={q} onChange={(e) => {
                    const next = [...questions]; next[i] = e.target.value; setQuestions(next);
                  }} placeholder={`Pregunta ${i+1}`} className="bg-black/40 border-white/10 text-white text-sm" />
                </div>
              ))}
              {questions.length < (session?.reading_type === "complete" ? 24 : 12) && (
                <button data-testid="add-question" onClick={() => setQuestions([...questions, ""])} className="text-xs text-[#d4af37] hover:underline flex items-center gap-1">+ Añadir otra pregunta</button>
              )}
            </div>

            <button data-testid="form-submit" disabled={submitting} onClick={submitForm} className="btn-gold w-full py-3 rounded-full uppercase text-xs tracking-widest mt-4 flex items-center justify-center gap-2">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Comenzar Lectura <ChevronRight className="w-4 h-4" /></>}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ----- IN PROGRESS reading -----
  const phaseInfo = phase ? PHASE_INFO[phase] : null;
  // Events shown in the in-progress view — restricted to the current phase so the
  // user can scroll back through prior draws of THIS phase only.
  const phaseEvents = events.filter((e) => e.phase === phase);
  const viewedEvent = viewIdx !== null && viewIdx >= 0 && viewIdx < phaseEvents.length
    ? phaseEvents[viewIdx]
    : (phaseEvents.length > 0 ? phaseEvents[phaseEvents.length - 1] : null);
  const currentEvent = viewedEvent;
  const isViewingPast = viewIdx !== null && viewIdx < phaseEvents.length - 1;
  const phases = PHASES_BY_TYPE[session.reading_type] || [];
  const currentPhaseIndex = phases.indexOf(phase);

  return (
    <div className="min-h-screen pt-20 pb-12 px-4 md:px-8" data-testid="lectura-in-progress">
      <MiniTutorial storageKey="ca_tut_lectura" title="Lectura Online" steps={LECTURA_TUTORIAL_STEPS} />
      <div className="max-w-5xl mx-auto">
        {/* Top bar */}
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-purple-300">{session.reading_label}</div>
            <h1 className="font-serif text-2xl md:text-3xl text-white">{phaseInfo?.label}</h1>
          </div>
          <div className="flex items-center gap-2">
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger data-testid="lang-select" className="bg-black/40 border-white/10 text-white w-32 h-9 text-xs"><Languages className="w-3 h-3 mr-1" /><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0a0a0a] border-[#d4af37]/30 text-white">{LANGUAGES.map((l) => <SelectItem key={l.code} value={l.code}>{l.name}</SelectItem>)}</SelectContent>
            </Select>
            <button data-testid="voice-toggle" onClick={() => {
              // The toggle click is a user gesture — perfect moment to
              // prime the SpeechSynthesis engine on mobile.
              primeSpeechOnce();
              setVoiceOn(!voiceOn);
              if (voiceOn) window.speechSynthesis?.cancel();
            }} className="bg-black/40 border border-white/10 rounded h-9 w-9 flex items-center justify-center text-neutral-300 hover:text-[#d4af37]">
              {voiceOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </button>
            {tl && (
              <div data-testid="time-left" className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border ${tl.expired ? "border-red-500/50 text-red-300 bg-red-500/10" : tl.mm < 5 ? "border-amber-500/50 text-amber-300 bg-amber-500/10" : "border-emerald-500/50 text-emerald-300 bg-emerald-500/10"}`}>
                <Clock className="w-3.5 h-3.5" />
                <span className="font-mono text-sm font-semibold">{String(tl.mm).padStart(2, "0")}:{String(tl.ss).padStart(2, "0")}</span>
              </div>
            )}
          </div>
        </div>

        {/* Progress bar */}
        <div className="flex items-center gap-1.5 mb-6 overflow-x-auto pb-2">
          {phases.map((p, i) => (
            <div key={p} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10px] uppercase tracking-widest whitespace-nowrap ${i === currentPhaseIndex ? "border-purple-400/60 bg-purple-500/20 text-purple-200" : i < currentPhaseIndex ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : "border-white/10 text-neutral-500"}`}>
              <span>{PHASE_INFO[p]?.icon}</span>
              {PHASE_INFO[p]?.label}
            </div>
          ))}
        </div>

        {/* Phase content */}
        <div className="glass-dark border border-purple-500/20 rounded-3xl p-6 md:p-10 mb-6">
          <AnimatePresence mode="wait">
            <motion.div key={phase} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              {/* Cards/runes display */}
              {currentEvent && currentEvent.phase === phase && (currentEvent.items_oriented?.length > 0 || currentEvent.items?.length > 0) && (
                <div className="flex flex-wrap justify-center gap-4 mb-6">
                  {(currentEvent.items_oriented && currentEvent.items_oriented.length > 0
                    ? currentEvent.items_oriented
                    : (currentEvent.items || []).map((nm) => ({ name: nm.replace(" (Invertida)", ""), reversed: nm.endsWith("(Invertida)") }))
                  ).map((it, i) => (
                    phase === "runes"
                      ? <RuneStone key={i} name={it.name} reversed={it.reversed} delay={i * 0.18} />
                      : <Card key={i} name={it.name} color={phaseInfo.color} reversed={it.reversed} delay={i * 0.18} />
                  ))}
                </div>
              )}

              {/* Previous-draw navigation (only when there are 2+ events in this phase) */}
              {phaseEvents.length > 1 && (
                <div className="flex items-center justify-center gap-3 mb-3 text-xs">
                  <button
                    data-testid="prev-draw"
                    onClick={() => {
                      try { window.speechSynthesis?.cancel(); } catch { /* ignore */ }
                      setViewIdx((idx) => Math.max(0, (idx === null ? phaseEvents.length - 1 : idx) - 1));
                    }}
                    disabled={(viewIdx === null ? phaseEvents.length - 1 : viewIdx) === 0}
                    className="px-3 py-1 rounded-full border border-white/20 text-neutral-300 hover:bg-white/5 disabled:opacity-30"
                  >← Anterior</button>
                  <span className="text-[11px] text-neutral-400 font-mono">
                    Tirada {(viewIdx === null ? phaseEvents.length : viewIdx + 1)} / {phaseEvents.length}
                    {isViewingPast && <span className="ml-2 text-amber-300">(viendo anterior)</span>}
                  </span>
                  <button
                    data-testid="next-draw"
                    onClick={() => {
                      try { window.speechSynthesis?.cancel(); } catch { /* ignore */ }
                      setViewIdx((idx) => {
                        const cur = idx === null ? phaseEvents.length - 1 : idx;
                        const nxt = cur + 1;
                        return nxt >= phaseEvents.length - 1 ? null : nxt;
                      });
                    }}
                    disabled={!isViewingPast}
                    className="px-3 py-1 rounded-full border border-white/20 text-neutral-300 hover:bg-white/5 disabled:opacity-30"
                  >Siguiente →</button>
                </div>
              )}

              {/* Interpretation */}
              {currentEvent && currentEvent.phase === phase && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }} className="bg-black/40 border border-purple-500/30 rounded-2xl p-5">
                  <div className="flex items-start gap-3">
                    <Sparkle className="w-5 h-5 text-purple-300 mt-1 flex-shrink-0" />
                    <p className="text-neutral-100 leading-relaxed whitespace-pre-wrap text-sm md:text-base">{currentEvent.interpretation}</p>
                  </div>
                  {voiceOn && (
                    <button onClick={() => speakText(currentEvent.interpretation, language, true)} className="mt-3 text-xs text-purple-300 hover:underline flex items-center gap-1"><Volume2 className="w-3 h-3" /> Repetir voz</button>
                  )}
                </motion.div>
              )}

              {/* Initial state OR continue drawing - draw button with counter */}
              {phase !== "summary" && phase !== "questions_live" && (
                <div className="text-center py-6">
                  {!currentEvent || currentEvent.phase !== phase ? (
                    <>
                      <div className="text-6xl mb-3">{phaseInfo?.icon}</div>
                      <h2 className="font-serif text-2xl text-white mb-2">Vamos con: {phaseInfo?.label}</h2>
                      <p className="text-neutral-400 text-sm mb-4">
                        {phase === "predictions" && "Voy a canalizar tus predicciones..."}
                        {phase === "additional" && "Una lectura adicional enfocada en tus dudas..."}
                        {(phase === "spanish_cards" || phase === "tarot" || phase === "runes") && `Realizaremos ${requiredFor(phase) || 1} tiradas para profundizar.`}
                      </p>
                    </>
                  ) : drawsInPhase(phase) < requiredFor(phase) && (
                    <div className="mb-2">
                      <span className="text-xs text-purple-300 uppercase tracking-widest">¿Quieres profundizar? Tira más cartas.</span>
                    </div>
                  )}
                  <div className="flex items-center justify-center gap-2 mb-3 flex-wrap">
                    <span data-testid="draws-counter" className="text-[11px] text-neutral-400 font-mono">
                      Tirada <span className="text-[#d4af37] font-bold">{drawsInPhase(phase)}</span> / {requiredFor(phase) || 1}
                    </span>
                    <div className="flex gap-0.5">
                      {Array.from({length: requiredFor(phase) || 1}).map((_, i) => (
                        <div key={i} className={`w-2 h-2 rounded-full ${i < drawsInPhase(phase) ? "bg-[#d4af37]" : "bg-neutral-700"}`} />
                      ))}
                    </div>
                  </div>
                  {drawsInPhase(phase) < requiredFor(phase) && (
                    <button data-testid="draw-btn" disabled={drawing} onClick={drawPhase} className="btn-gold py-3 px-8 rounded-full uppercase text-xs tracking-widest inline-flex items-center gap-2 disabled:opacity-50">
                      {drawing ? <><Loader2 className="w-4 h-4 animate-spin" /> Canalizando...</> : <><Sparkle className="w-4 h-4" /> {drawsInPhase(phase) === 0 ? `Iniciar ${phaseInfo?.label}` : `Tirar ${phaseInfo?.n || 4} cartas más`}</>}
                    </button>
                  )}
                  {drawsInPhase(phase) >= requiredFor(phase) && requiredFor(phase) > 0 && (
                    <div className="text-emerald-300 text-sm mt-2 flex items-center justify-center gap-1.5">
                      <CheckCircle2 className="w-4 h-4" /> Fase completa. Avanza a la siguiente.
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {/* Live questions section (only "complete" type) */}
          {phase === "questions_live" && session.reading_type === "complete" && (
            <div className="mt-6 border-t border-white/10 pt-6">
              <h3 className="font-serif text-lg text-white mb-3">Haz tu pregunta en vivo</h3>
              <div className="flex gap-2 mb-3">
                <Input data-testid="live-q-input" value={liveQ} onChange={(e) => setLiveQ(e.target.value)} placeholder="Escribe tu pregunta..." className="bg-black/40 border-white/10 text-white" onKeyDown={(e) => e.key === "Enter" && askLive()} />
                <button data-testid="live-q-send" disabled={asking || !liveQ.trim()} onClick={askLive} className="btn-gold px-5 rounded-full uppercase text-xs tracking-widest flex items-center gap-1 disabled:opacity-50">
                  {asking ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-3.5 h-3.5" /> Enviar</>}
                </button>
              </div>
              {/* History of live questions */}
              {events.filter(e => e.phase === "questions_live").length > 0 && (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {events.filter(e => e.phase === "questions_live").map((ev) => (
                    <div key={ev.id} className="bg-black/40 border border-emerald-500/20 rounded p-3 text-sm">
                      <div className="text-[#d4af37] font-semibold mb-1">❓ {ev.items?.[0]}</div>
                      <div className="text-neutral-200 whitespace-pre-wrap">{ev.interpretation}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Advance / Finish — siempre permite avanzar (con confirmación si faltan tiradas) */}
          {phase !== "summary" && (currentEvent?.phase === phase || phase === "questions_live" || drawsInPhase(phase) === 0) && (
            <div className="flex justify-between items-center mt-6 flex-wrap gap-2">
              <div className="text-xs text-neutral-500">
                {!canAdvance(phase) && phase !== "questions_live" && (
                  <span className="text-amber-400">Faltan {requiredFor(phase) - drawsInPhase(phase)} tirada(s) — puedes saltarte si lo deseas</span>
                )}
              </div>
              <button data-testid="next-phase-btn" onClick={() => advancePhase(false)} className="btn-outline-gold py-2.5 px-6 rounded-full uppercase text-xs tracking-widest inline-flex items-center gap-2">
                {!canAdvance(phase) && phase !== "questions_live" && phase !== "summary" ? "Saltar fase" : (currentPhaseIndex >= phases.length - 2 ? "Iniciar cierre" : "Siguiente fase")} <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {/* History */}
        {events.length > 0 && (
          <details className="mb-6">
            <summary className="cursor-pointer text-xs text-neutral-400 hover:text-[#d4af37] mb-2">📜 Ver historial completo de la lectura ({events.length} eventos)</summary>
            <div className="space-y-2 mt-3">
              {events.map((ev) => (
                <div key={ev.id} className="bg-black/30 border border-white/5 rounded p-3 text-xs">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-purple-300 font-semibold">{PHASE_INFO[ev.phase]?.icon} {ev.kind}</span>
                    <span className="text-[10px] text-neutral-500">{new Date(ev.at).toLocaleTimeString()}</span>
                  </div>
                  {ev.items?.length > 0 && <div className="text-[#d4af37] italic mb-1">{ev.items.join(" · ")}</div>}
                  <div className="text-neutral-300 whitespace-pre-wrap">{ev.interpretation}</div>
                </div>
              ))}
              <div ref={eventsEndRef} />
            </div>
          </details>
        )}

        <Link to="/" className="text-xs text-neutral-500 hover:text-[#d4af37] inline-flex items-center gap-1"><Home className="w-3 h-3" /> Salir y volver al inicio</Link>
      </div>
    </div>
  );
}
